// src/components/admin/TabCodigosCanje.js
// Tab de administración del módulo Códigos de Canje.
//
// Vista principal: listado paginado con filtros por estado, cooperativa,
// aptos, búsqueda. Al hacer clic en una fila abre un dialog de detalle
// con el QR, historial de consumos, tickets y acciones (aprobar emisión,
// anular, registrar consumo manual, aprobar entrada como asociado).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Stack, Chip, Alert, Button,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  CircularProgress, Divider, LinearProgress, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  listarCodigosCanje, getCodigoCanje,
  aprobarEmisionCodigo, anularCodigoCanje,
  aprobarAsociadoCanje, registrarConsumoManual, subirTicketConsumo,
} from '../../services/codigosCanjeApi';

const PAGE_SIZE = 25;

const ESTADO_COLOR = {
  PENDIENTE_APROBACION: 'warning',
  EMITIDO:              'info',
  EN_USO:               'primary',
  CONSUMIDO:            'default',
  EXPIRADO:             'default',
  ANULADO:              'error',
};
const ESTADOS = ['PENDIENTE_APROBACION','EMITIDO','EN_USO','CONSUMIDO','EXPIRADO','ANULADO'];

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString('es-CO')}`;
}

export default function TabCodigosCanje({ onToast }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [page, setPage]         = useState(1);
  const [estado, setEstado]     = useState('');
  const [aptos, setAptos]       = useState('');
  const [buscar, setBuscar]     = useState('');

  // Dialog detalle
  const [detalle, setDetalle]   = useState(null);  // { codigo, consumos, qrPngBase64 }
  const [detalleLoading, setDetalleLoading] = useState(false);

  // Dialogs de acciones
  const [dlgAnular, setDlgAnular]         = useState({ open: false, id: null, motivo: '' });
  const [dlgConsumo, setDlgConsumo]       = useState({ open: false, aliadoID: '', canal: 'POS', pedidoExternoRef: '', monto: '' });
  const [accionEnCurso, setAccionEnCurso] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listarCodigosCanje({
        estado: estado || undefined,
        aptos:  aptos === 'true' ? true : aptos === 'false' ? false : undefined,
        buscar: buscar.trim() || undefined,
        page, pageSize: PAGE_SIZE,
      });
      setItems(Array.isArray(r.items) ? r.items : []);
      setTotal(r.total || 0);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el listado.', 'error');
    } finally { setLoading(false); }
  }, [estado, aptos, buscar, page, onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (id) => {
    setDetalleLoading(true);
    setDetalle({ codigo: { CodigoCanjeID: id }, consumos: [], qrPngBase64: null });
    try {
      const r = await getCodigoCanje(id);
      setDetalle(r);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el detalle.', 'error');
      setDetalle(null);
    } finally { setDetalleLoading(false); }
  };

  const recargarDetalle = async () => {
    if (!detalle?.codigo?.codigoCanjeID) return;
    try {
      const r = await getCodigoCanje(detalle.codigo.codigoCanjeID);
      setDetalle(r);
    } catch { /* silencio */ }
  };

  const handleAprobarEmision = async () => {
    if (!detalle?.codigo?.codigoCanjeID) return;
    setAccionEnCurso(true);
    try {
      await aprobarEmisionCodigo(detalle.codigo.codigoCanjeID);
      onToast?.('Emisión aprobada. El código ya puede usarse.', 'success');
      await recargarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al aprobar.', 'error');
    } finally { setAccionEnCurso(false); }
  };

  const abrirAnular = () => setDlgAnular({ open: true, id: detalle.codigo.codigoCanjeID, motivo: '' });
  const handleAnular = async () => {
    setAccionEnCurso(true);
    try {
      await anularCodigoCanje(dlgAnular.id, dlgAnular.motivo);
      onToast?.('Código anulado.', 'success');
      setDlgAnular({ open: false, id: null, motivo: '' });
      await recargarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al anular.', 'error');
    } finally { setAccionEnCurso(false); }
  };

  const handleAprobarAsociado = async () => {
    if (!detalle?.codigo?.codigoCanjeID) return;
    if (!window.confirm('¿Aprobar la entrada como asociado? El afiliado pasará a ACTIVO y su rol cambiará a Afiliado. Esta acción es definitiva.'))
      return;
    setAccionEnCurso(true);
    try {
      const r = await aprobarAsociadoCanje(detalle.codigo.codigoCanjeID);
      onToast?.(r.mensaje || 'Entrada aprobada.', 'success');
      await recargarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al aprobar entrada.', 'error');
    } finally { setAccionEnCurso(false); }
  };

  const abrirConsumo = () => setDlgConsumo({ open: true, aliadoID: '', canal: 'POS', pedidoExternoRef: '', monto: '' });
  const handleRegistrarConsumo = async () => {
    setAccionEnCurso(true);
    try {
      await registrarConsumoManual(detalle.codigo.codigoCanjeID, {
        aliadoID: parseInt(dlgConsumo.aliadoID, 10),
        canal: dlgConsumo.canal,
        pedidoExternoRef: dlgConsumo.pedidoExternoRef.trim(),
        monto: parseFloat(dlgConsumo.monto),
      });
      onToast?.('Consumo registrado.', 'success');
      setDlgConsumo({ open: false, aliadoID: '', canal: 'POS', pedidoExternoRef: '', monto: '' });
      await recargarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al registrar consumo.', 'error');
    } finally { setAccionEnCurso(false); }
  };

  const handleSubirTicket = async (consumoId, file) => {
    if (!file) return;
    try {
      await subirTicketConsumo(detalle.codigo.codigoCanjeID, consumoId, file);
      onToast?.('Ticket subido.', 'success');
      await recargarDetalle();
    } catch (e) {
      onToast?.(e.message || 'Error al subir ticket.', 'error');
    }
  };

  const copiarCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo)
      .then(() => onToast?.(`Copiado: ${codigo}`, 'success'))
      .catch(() => onToast?.('No se pudo copiar.', 'error'));
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const cod = detalle?.codigo;
  const puedeAprobarEmision = cod?.estado === 'PENDIENTE_APROBACION';
  const puedeAnular         = cod && cod.estado !== 'ANULADO' && cod.estado !== 'CONSUMIDO' && !cod.asociadoAfiliadoID;
  const puedeAprobarAsociado = cod?.aptoParaAsociarse && !cod?.asociadoAfiliadoID;
  const puedeRegistrarConsumo = cod && (cod.estado === 'EMITIDO' || cod.estado === 'EN_USO');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <RedeemIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a4d2e', lineHeight: 1 }}>
            Códigos de canje
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mecanismo alternativo de afiliación para candidatos sin capacidad de pago
          </Typography>
        </Box>
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField size="small" fullWidth placeholder="Código, nombre, documento, email…"
              value={buscar} onChange={e => { setBuscar(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select value={estado} label="Estado"
                onChange={e => { setEstado(e.target.value); setPage(1); }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                {ESTADOS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Aptos</InputLabel>
              <Select value={aptos} label="Aptos"
                onChange={e => { setAptos(e.target.value); setPage(1); }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                <MenuItem value="true">Solo aptos para asociarse</MenuItem>
                <MenuItem value="false">Solo no aptos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="caption" color="text.secondary">
              {total} código{total === 1 ? '' : 's'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tabla */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Solicitante</TableCell>
              <TableCell>Cooperativa</TableCell>
              <TableCell align="center">Usos</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Apto</TableCell>
              <TableCell>Emitido</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(x => (
              <TableRow key={x.codigoCanjeID} hover sx={{ cursor: 'pointer' }}
                onClick={() => abrirDetalle(x.codigoCanjeID)}>
                <TableCell>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, color: '#1a4d2e' }}>
                    {x.codigo}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ fontSize: '0.85rem' }}>{x.solicitanteNombre || '—'}</Box>
                  <Box sx={{ fontSize: '0.72rem', color: '#888' }}>{x.solicitanteDocumento || ''}</Box>
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem' }}>{x.cooperativa || '—'}</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'monospace' }}>
                  {x.usosActuales}/{x.usosMaximos}
                </TableCell>
                <TableCell align="center">
                  <Chip label={x.estado} size="small" color={ESTADO_COLOR[x.estado] || 'default'}
                    sx={{ fontSize: '0.65rem', fontWeight: 700 }} />
                </TableCell>
                <TableCell align="center">
                  {x.aptoParaAsociarse
                    ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                    : <span style={{ color: '#ccc' }}>—</span>}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {new Date(x.fechaEmision).toLocaleDateString('es-CO')}
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <Tooltip title="Copiar código">
                    <IconButton size="small" onClick={() => copiarCodigo(x.codigo)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#888' }}>
                  Sin códigos. Emite uno desde el detalle de una solicitud en el tab Solicitudes.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <Typography variant="body2" sx={{ px: 2, alignSelf: 'center' }}>
            Página {page} de {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </Stack>
      )}

      {/* ── Dialog de detalle ─────────────────────────────────────── */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <QrCode2Icon />
            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {cod?.codigo || 'Detalle del código'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {cod?.solicitanteNombre || ''} — {cod?.solicitanteDocumento || ''}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setDetalle(null)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: '24px !important' }}>
          {detalleLoading && <LinearProgress sx={{ mb: 2 }} />}

          {cod && !detalleLoading && (
            <>
              <Grid container spacing={3}>
                {/* QR */}
                <Grid item xs={12} sm={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fafafa' }}>
                    {detalle.qrPngBase64
                      ? <img src={`data:image/png;base64,${detalle.qrPngBase64}`}
                             alt="QR" style={{ maxWidth: 180, width: '100%' }} />
                      : <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                          Sin QR
                        </Box>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Escanea para presentar el código
                    </Typography>
                    <Button size="small" startIcon={<ContentCopyIcon />}
                      onClick={() => copiarCodigo(cod.codigo)} sx={{ mt: 1 }}>
                      Copiar código
                    </Button>
                  </Paper>
                </Grid>

                {/* Info principal */}
                <Grid item xs={12} sm={8}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={cod.estado} color={ESTADO_COLOR[cod.estado] || 'default'}
                      sx={{ fontWeight: 700 }} />
                    {cod.aptoParaAsociarse && (
                      <Chip label="APTO PARA ASOCIARSE" color="success" icon={<CheckCircleIcon />} />
                    )}
                    {cod.asociadoAfiliadoID && (
                      <Chip label={`YA ASOCIADO #${cod.asociadoAfiliadoID}`} color="primary" icon={<EmojiEventsIcon />} />
                    )}
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Nombre</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>{cod.solicitanteNombre || '—'}</Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Documento</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>{cod.solicitanteDocumento || '—'}</Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Email</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>{cod.solicitanteEmail || '—'}</Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Cooperativa</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>{cod.cooperativa || '—'}</Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Usos</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {cod.usosActuales}/{cod.usosMaximos}
                    </Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Monto acumulado</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a4d2e' }}>
                      {fmtMoney(cod.montoAcumulado)}
                    </Grid>
                    <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Emitido</Grid>
                    <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>
                      {new Date(cod.fechaEmision).toLocaleString('es-CO')}
                      {cod.emitidoPor && <> · por {cod.emitidoPor}</>}
                    </Grid>
                    {cod.fechaVencimiento && (
                      <>
                        <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Vence</Grid>
                        <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>
                          {new Date(cod.fechaVencimiento).toLocaleString('es-CO')}
                        </Grid>
                      </>
                    )}
                    {cod.fechaAnulacion && (
                      <>
                        <Grid item xs={5} sx={{ color: '#666', fontSize: '0.85rem' }}>Anulado</Grid>
                        <Grid item xs={7} sx={{ fontSize: '0.85rem' }}>
                          {new Date(cod.fechaAnulacion).toLocaleString('es-CO')}
                          {cod.motivoAnulacion && <>: {cod.motivoAnulacion}</>}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }}>
                <Chip label="Consumos" size="small" icon={<ReceiptLongIcon />} />
              </Divider>

              {(detalle.consumos || []).length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Aún no se han registrado consumos con este código.
                </Alert>
              ) : (
                <TableContainer variant="outlined" component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Aliado</TableCell>
                        <TableCell>Canal</TableCell>
                        <TableCell>Pedido</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell align="center">Ticket</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalle.consumos.map(c => (
                        <TableRow key={c.consumoID}>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {new Date(c.fechaConsumo).toLocaleString('es-CO')}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem' }}>
                            {c.aliado || `#${c.aliadoID}`}
                            {c.aliadoCodigo && <div style={{ color:'#888', fontSize:'0.72rem' }}>{c.aliadoCodigo}</div>}
                          </TableCell>
                          <TableCell>
                            <Chip label={c.canal} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {c.pedidoExternoRef}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(c.monto)}</TableCell>
                          <TableCell align="center">
                            {c.ticketUrl ? (
                              <Tooltip title="Ticket adjunto">
                                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Subir ticket">
                                <IconButton size="small" component="label">
                                  <UploadFileIcon fontSize="small" />
                                  <input hidden type="file"
                                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                                    onChange={e => handleSubirTicket(c.consumoID, e.target.files?.[0])} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          {cod && !detalleLoading && (
            <>
              {puedeAprobarEmision && (
                <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
                  onClick={handleAprobarEmision} disabled={accionEnCurso}>
                  Aprobar emisión
                </Button>
              )}
              {puedeRegistrarConsumo && (
                <Button variant="outlined" startIcon={<ReceiptLongIcon />}
                  onClick={abrirConsumo} disabled={accionEnCurso}>
                  Registrar consumo manual
                </Button>
              )}
              {puedeAnular && (
                <Button variant="outlined" color="error" startIcon={<BlockIcon />}
                  onClick={abrirAnular} disabled={accionEnCurso}>
                  Anular
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {puedeAprobarAsociado && (
                <Button variant="contained" color="success" size="large"
                  startIcon={<EmojiEventsIcon />}
                  onClick={handleAprobarAsociado} disabled={accionEnCurso}
                  sx={{ bgcolor: '#1a4d2e', '&:hover': { bgcolor: '#2e7d32' }, fontWeight: 700 }}>
                  Aprobar entrada como asociado
                </Button>
              )}
              <Button onClick={() => setDetalle(null)} disabled={accionEnCurso}>Cerrar</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog anular */}
      <Dialog open={dlgAnular.open} onClose={() => setDlgAnular({ open: false, id: null, motivo: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Anular código de canje</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción marca el código como ANULADO y no permite más consumos.
          </Alert>
          <TextField label="Motivo (obligatorio)" fullWidth multiline rows={3}
            value={dlgAnular.motivo} onChange={e => setDlgAnular(p => ({ ...p, motivo: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgAnular({ open: false, id: null, motivo: '' })} disabled={accionEnCurso}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleAnular}
            disabled={accionEnCurso || !dlgAnular.motivo.trim()}
            startIcon={accionEnCurso ? <CircularProgress size={14} color="inherit" /> : <BlockIcon />}>
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog consumo manual */}
      <Dialog open={dlgConsumo.open} onClose={() => setDlgConsumo({ open: false, aliadoID: '', canal: 'POS', pedidoExternoRef: '', monto: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar consumo manual</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Registra una compra que el candidato ya realizó y presentó al admin (por WhatsApp, presencial, etc.).
          </Alert>
          <Stack spacing={2}>
            <TextField label="ID del aliado" type="number" fullWidth
              value={dlgConsumo.aliadoID} onChange={e => setDlgConsumo(p => ({ ...p, aliadoID: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select value={dlgConsumo.canal} label="Canal"
                onChange={e => setDlgConsumo(p => ({ ...p, canal: e.target.value }))}>
                <MenuItem value="POS">POS presencial</MenuItem>
                <MenuItem value="MARKETPLACE">Marketplace</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Referencia externa del pedido / ticket físico" fullWidth
              value={dlgConsumo.pedidoExternoRef} onChange={e => setDlgConsumo(p => ({ ...p, pedidoExternoRef: e.target.value }))} />
            <TextField label="Monto (COP)" type="number" fullWidth
              value={dlgConsumo.monto} onChange={e => setDlgConsumo(p => ({ ...p, monto: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgConsumo({ open: false, aliadoID: '', canal: 'POS', pedidoExternoRef: '', monto: '' })}
            disabled={accionEnCurso}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleRegistrarConsumo}
            disabled={accionEnCurso ||
              !dlgConsumo.aliadoID || !dlgConsumo.pedidoExternoRef.trim() ||
              !dlgConsumo.monto || parseFloat(dlgConsumo.monto) <= 0}
            startIcon={accionEnCurso ? <CircularProgress size={14} color="inherit" /> : <ReceiptLongIcon />}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
